import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  useTheme,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useOfferConfig } from '../contexts/OfferConfigContext';
import {
  useCreateOffer,
  useUpdateOffer,
  useOffer,
  useOfferTemplates,
  useCreateOfferTemplate,
} from '../hooks/useOffers';
import { Select, MultiSelect, ImageUpload, JsonEditor, LoadingSpinner } from '@shared/components';
import { offerValidationSchema } from '../utils/validation';
import {
  formatPremiumIds,
  parsePremiumIds,
  formatDateForAPI,
  parseDateFromAPI,
  buildCustomFieldDefaults,
  buildCustomFieldsPayload,
  getEnvironment,
  getSyncOnValue,
  getOfferId,
} from '../utils';
import { OfferPromotionDTO, OfferScheduleDTO } from '../types';
import { PremiumIdInput, ConfirmDialog } from '@modules/offers/shared';
import { usePlaybookFields } from '@modules/playbook/hooks/usePlaybook';
import { DynamicFieldRenderer } from '@modules/playbook/components/DynamicFieldRenderer';
import { PageHeader } from '@shared/components/PageHeader';
import FieldGroup from '@modules/offers/components/AddEditOffer/FieldGroup';
import SaveTemplateDialog from '@modules/offers/components/AddEditOffer/SaveTemplateDialog';
import { ScheduleButtons } from '@modules/offers/components/AddEditOffer/ScheduleButtons';
import { OfferDetailsSection } from '@modules/offers/components/AddEditOffer/OfferDetailsSection';
import { FormField } from '@shared/components/FormField/FormField';
import { OFFER_FIELD_TOOLTIPS } from '../constants/fieldTooltips';
import { OfferFlags } from '@modules/offers/components/AddEditOffer/OfferFlags';
import { SyncEnvironment, SyncSection } from '@modules/offers/components/AddEditOffer/SyncSection';
import { ScheduleDrawer } from '@modules/offers/components/Drawers/ScheduleDrawer';
import { PromotionDrawer } from '@modules/offers/components/Drawers/PromotionDrawer';

interface CardSectionProps {
  title: string;
  children: React.ReactNode;
  helperText?: string;
}

const CardSection: React.FC<CardSectionProps> = ({ title, children, helperText }) => {
  const theme = useTheme();
  return (
    <Paper sx={{ p: 3, mb: 3, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
          {title}
        </Typography>
        {helperText && (
          <Tooltip title={helperText} arrow placement="top">
            <IconButton size="small" sx={{ p: 0, ml: 0.2, height: 16 }}>
              <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider sx={{ mb: 3 }} />
      {children}
    </Paper>
  );
};

const AddEditOffer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // const theme = useTheme();
  const isEditing = Boolean(id);

  // Inline drawer state
  const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
  const [promotionDrawerOpen, setPromotionDrawerOpen] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState<OfferScheduleDTO | undefined>(undefined);
  const [promotionDraft, setPromotionDraft] = useState<OfferPromotionDTO | undefined>(undefined);

  // Sync confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const [syncOnChanged, setSyncOnChanged] = useState(false);

  // Template state
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [hasFormChanged, setHasFormChanged] = useState(false);
  const [isTemplateSelected, setIsTemplateSelected] = useState(false);

  const {
    premiumOptions,
    scopeOptions,
    currencyOptions,
    categoryOptions,
    countryOptions,
    priceOptions,
    isLoading: configLoading,
  } = useOfferConfig();

  const { data: existingOffer, isLoading: offerLoading } = useOffer(isEditing ? Number.parseInt(id) : 0);

  // Playbook dynamic fields for Offer section
  const { data: dynamicFieldList, isLoading: dynamicFieldsLoading } = usePlaybookFields('Offer');

  const createMutation = useCreateOffer();
  const updateMutation = useUpdateOffer(isEditing ? Number.parseInt(id) : 0);
  const { data: templates } = useOfferTemplates();
  const createTemplateMutation = useCreateOfferTemplate();

  const getOfferSubtitle = useMemo(() => {
    if (existingOffer && isEditing) {
      getOfferId(existingOffer);
    } else {
      return 'Fill out the form below to create a new offer.';
    }
  }, [isEditing, existingOffer, id]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    watch,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(offerValidationSchema),
    defaultValues: {
      offer_name: '',
      scope: [],
      price: null,
      currency: null,
      offer_category: null,
      duration: null,
      premium_ids: [],
      start_date: '',
      end_date: '',
      quantity: 0,
      country: [],
      comment: '',
      custom_name: {
        en: '',
        de: '',
      },
      trigger_login_reward: { itemID: '', delay: '', type: '' },
      starter_offer: false,
      min_user_age: null,
      max_user_age: null,
      min_ltv: null,
      max_ltv: null,
      desktop_background: '',
      mobile_background: '',
      mobile_icon: '',
      popup_background: '',
      popup_icon: '',
      is_featured: false,
      is_test_offer: false,
      user_id: '',
      is_shop_default: false,
      hero_item_id: null,
      level_item_id: null,
      medal_item_id: null,
      required_medals: null,
      sync_on: 'draft',
    },
    mode: 'onSubmit',
    shouldFocusError: true,
  });

  useEffect(() => {
    const firstErrorKey = Object.keys(errors)[0];
    if (firstErrorKey) {
      const errorElement = document.querySelector(`[name="${firstErrorKey}"]`);

      if (errorElement && typeof errorElement.scrollIntoView === 'function') {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLElement).focus?.();
      } else {
        // fallback: find input within a container that has data-testid or label
        const fallbackElement = document.querySelector(
          `[data-testid*="${firstErrorKey}"], [aria-label*="${firstErrorKey}"]`,
        );
        if (fallbackElement && typeof fallbackElement.scrollIntoView === 'function') {
          fallbackElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (fallbackElement as HTMLElement).focus?.();
        }
      }
    }
  }, [errors]);

  // Load existing offer data when editing
  useEffect(() => {
    // Only run when we have both offer data and dynamic fields (or when dynamic fields loading is complete)
    if (isEditing && existingOffer && !dynamicFieldsLoading) {
      const customFieldDefaults = buildCustomFieldDefaults(existingOffer?.custom_fields ?? [], dynamicFieldList ?? []);

      const formData = {
        offer_name: existingOffer.offer_name ?? '',
        scope: existingOffer.scope ? [existingOffer.scope] : [],
        price: existingOffer.price ?? null,
        currency: existingOffer.currency ?? null,
        offer_category: existingOffer.offer_category ?? null,
        duration: existingOffer.duration ?? null,
        premium_ids: existingOffer.premium_ids ? existingOffer.premium_ids.split(',') : [],
        start_date: existingOffer.start_date ? formatDateForAPI(existingOffer.start_date) : '',
        end_date: existingOffer.end_date ? formatDateForAPI(existingOffer.end_date) : '',
        quantity: existingOffer.quantity ?? 0,
        country: existingOffer.country ? existingOffer.country.split(',').map((c) => Number.parseInt(c)) : [],
        comment: existingOffer.comment ?? '',
        custom_name: existingOffer.custom_name ?? {},
        trigger_login_reward: existingOffer.trigger_login_reward ?? {},
        starter_offer: existingOffer.starter_offer ?? false,
        min_user_age: existingOffer.min_user_age ?? null,
        max_user_age: existingOffer.max_user_age ?? null,
        min_ltv: existingOffer.min_ltv ?? null,
        max_ltv: existingOffer.max_ltv ?? null,
        desktop_background: existingOffer.desktop_background ?? '',
        mobile_background: existingOffer.mobile_background ?? '',
        mobile_icon: existingOffer.mobile_icon ?? '',
        popup_background: existingOffer.popup_background ?? '',
        popup_icon: existingOffer.popup_icon ?? '',
        is_featured: existingOffer.is_featured ?? false,
        is_test_offer: existingOffer.is_test_offer ?? false,
        user_id: existingOffer.user_id ?? '',
        is_shop_default: existingOffer.is_shop_default ?? false,
        hero_item_id: existingOffer.hero_item_id ?? null,
        level_item_id: existingOffer.level_item_id ?? null,
        medal_item_id: existingOffer.medal_item_id ?? null,
        required_medals: existingOffer.required_medals ?? null,
        // Map backend sync_on value: staging -> sandbox for UI
        sync_on: getEnvironment(existingOffer.sync_on),
        custom_fields: { ...customFieldDefaults },
      };
      reset(formData);

      if (existingOffer.offer_schedule?.[0]) {
        const schedule = existingOffer.offer_schedule[0];
        delete schedule.created_at;
        delete schedule.updated_at;
        delete schedule.deleted_at;
        setScheduleDraft({ ...schedule });
      }
      if (existingOffer.offer_promotion?.[0]) {
        const promotion = existingOffer.offer_promotion[0];
        delete promotion.created_at;
        delete promotion.updated_at;
        delete promotion.deleted_at;
        setPromotionDraft({ ...promotion });
      }
    }
  }, [existingOffer, isEditing, reset, dynamicFieldList, dynamicFieldsLoading]);

  const onSubmit = async (data) => {
    // Check if sync_on has changed in edit mode
    // Map the existing offer's sync_on for comparison (staging -> sandbox)
    const existingSyncOn = getEnvironment(existingOffer?.sync_on);
    const syncChanged = isEditing && existingOffer && data.sync_on !== existingSyncOn;

    // Show confirmation dialog for create mode or when sync_on changes in edit mode
    if (!isEditing || syncChanged) {
      setPendingFormData(data);
      setSyncOnChanged(syncChanged);
      setConfirmDialogOpen(true);
      return;
    }

    // For edit mode without sync_on change, proceed directly
    await saveOffer(data);
  };

  const saveOffer = async (data) => {
    try {
      const payload = {
        ...data,
        premium_ids: formatPremiumIds(parsePremiumIds(data.premium_ids)),
        ...(promotionDraft
          ? { promotion: { ...promotionDraft, offer_id: isEditing ? Number.parseInt(id) : promotionDraft.offer_id } }
          : {}),
        ...(scheduleDraft ? { schedule: { ...scheduleDraft } } : {}),
        custom_fields: buildCustomFieldsPayload(data.custom_fields, dynamicFieldList),
        // Map sync_on value: draft -> draft, sandbox -> staging, production -> production
        sync_on: data.sync_on === SyncEnvironment.SANDBOX ? 'staging' : data.sync_on,
      };

      if (isEditing) {
        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }
    } catch (error) {
      console.error('Failed to save offer:', error);
    }
  };

  const handleConfirmSync = async () => {
    setConfirmDialogOpen(false);
    if (pendingFormData) {
      await saveOffer({ ...pendingFormData, sync_on: getSyncOnValue(pendingFormData.sync_on) });
      setPendingFormData(null);
    }
  };

  const handleCancelSync = () => {
    setConfirmDialogOpen(false);
    setPendingFormData(null);
  };

  const getSyncConfirmationMessage = (sync_on: string) => {
    const prefix = syncOnChanged ? 'The sync environment will be changed. ' : '';
    switch (sync_on) {
      case SyncEnvironment.DRAFT:
        return prefix + 'This offer will be saved in draft mode. No data will be sent to any system.';
      case SyncEnvironment.SANDBOX:
        return prefix + 'This offer will be saved in sandbox mode. Data will be pushed to the sandbox environment.';
      case SyncEnvironment.PRODUCTION:
        return (
          prefix + 'This offer will be saved in production mode. Data will be pushed to the production environment.'
        );
      default:
        return prefix + 'This offer will be saved.';
    }
  };

  const handleCancel = () => {
    navigate('/offers');
  };

  // Template handlers
  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setHasFormChanged(false);
      setIsTemplateSelected(false);
      return;
    }

    const template = templates?.find((t) => t.offer_template_id === templateId);
    if (!template) return;

    const templateData = template.template_data;
    reset({
      ...templateData,
      premium_ids: templateData.premium_ids || [],
      sync_on: 'draft', // Always start as draft
    });
    setIsTemplateSelected(true); // Mark template as selected
    setHasFormChanged(false); // Reset form changed flag
  };

  const handleSaveTemplate = async (templateName: string) => {
    const formData = watch();
    // blacklisting fields
    const data = { ...formData, start_date: '', end_date: '' };
    await createTemplateMutation.mutateAsync({
      template_name: templateName,
      template_data: data,
    });
    setSaveTemplateDialogOpen(false);
  };

  // Track form changes
  useEffect(() => {
    const subscription = watch(() => {
      if (isTemplateSelected || !isEditing) {
        setHasFormChanged(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, isTemplateSelected, isEditing]);

  if (configLoading || (isEditing && offerLoading)) {
    return <LoadingSpinner fullHeight message="Loading offer data..." />;
  }

  const mutation = isEditing ? updateMutation : createMutation;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ mx: 'auto', p: 1 }}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <PageHeader
            title={isEditing ? 'Edit Offer' : 'Create New Offer'}
            subtitle={getOfferSubtitle}
            showBackButton={isEditing}
            onBack={() => navigate(-1)}
            backLabel="Back"
            primaryActionLabel={isEditing ? 'Edit Offer' : 'Create Offer'}
            onPrimaryAction={handleSubmit(onSubmit)}
            isSubmitting={isSubmitting}
            primaryActionDisabled={!isEditing && !isTemplateSelected && !hasFormChanged}
            secondaryActions={
              !isEditing ? (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {templates && templates.length > 0 && (
                    <Box sx={{ minWidth: 250, height: '100%' }}>
                      <Select
                        value={selectedTemplateId}
                        onChange={(value) => handleTemplateSelect(String(value))}
                        options={[
                          { optionId: 0, label: 'Select Template', value: '' },
                          ...templates.map((t, index) => ({
                            optionId: index + 1,
                            label: t.template_name,
                            value: t.offer_template_id,
                          })),
                        ]}
                        placeholder="Select a template"
                      />
                    </Box>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => setSaveTemplateDialogOpen(true)}
                    disabled={isSubmitting || !hasFormChanged}
                    data-testid="save-template-button"
                    sx={{ height: '100%' }}
                  >
                    Make Template
                  </Button>
                </Box>
              ) : undefined
            }
          />

          {mutation.isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {mutation.error?.message || 'Failed to save offer'}
            </Alert>
          )}

          {/* Two Column Layout for Better Space Usage */}
          <Grid container spacing={3}>
            {/* Left Column - Basic Info & Pricing */}
            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
              <CardSection title="Offer Details">
                <OfferDetailsSection
                  control={control}
                  errors={errors}
                  isEditing={isEditing}
                  scopeOptions={scopeOptions}
                  categoryOptions={categoryOptions}
                  currencyOptions={currencyOptions}
                  priceOptions={priceOptions}
                  watch={watch}
                  setValue={setValue}
                />
              </CardSection>
            </Grid>

            {/* Right Column - Schedule & Targeting */}
            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
              <CardSection title="Availability & Targeting">
                <Stack spacing={2}>
                  <FieldGroup>
                    <Box sx={{ flex: 1 }}>
                      <FormField label="Start Date" tooltip={OFFER_FIELD_TOOLTIPS.start_date} />
                      <Controller
                        name="start_date"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            label=""
                            value={field.value ? parseDateFromAPI(field.value) : null}
                            onChange={(date) => field.onChange(date ? formatDateForAPI(date) : '')}
                            minDate={new Date()}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.start_date,
                                helperText: errors.start_date?.message || ' ',
                                size: 'small',
                                FormHelperTextProps: {
                                  sx: { minHeight: '25px' },
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <FormField label="End Date" tooltip={OFFER_FIELD_TOOLTIPS.end_date} />
                      <Controller
                        name="end_date"
                        control={control}
                        render={({ field }) => (
                          <DatePicker
                            label=""
                            value={field.value ? parseDateFromAPI(field.value) : null}
                            onChange={(date) => field.onChange(date ? formatDateForAPI(date) : '')}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                error: !!errors.end_date,
                                helperText: errors.end_date?.message || ' ',
                                size: 'small',
                                FormHelperTextProps: {
                                  sx: { minHeight: '25px' },
                                },
                              },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </FieldGroup>

                  <Box>
                    <FormField label="Countries" tooltip={OFFER_FIELD_TOOLTIPS.country} />
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          value={field.value}
                          onChange={field.onChange}
                          options={countryOptions}
                          error={errors.country?.message}
                          data-testid="country-select"
                          useOptionId
                          placeholder="Select countries"
                        />
                      )}
                    />
                  </Box>

                  {/* Offer Flags */}
                  <OfferFlags control={control} errors={errors} />
                  <Box />
                  <ScheduleButtons
                    scheduleDraft={scheduleDraft}
                    startDate={watch('start_date')}
                    endDate={watch('end_date')}
                    onScheduleClick={() => setScheduleDrawerOpen(true)}
                    onPromotionClick={() => setPromotionDrawerOpen(true)}
                  />
                </Stack>
              </CardSection>
            </Grid>
          </Grid>

          {/* Premium Rewards & Segmentation - Two Column */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
              <CardSection title="Premium Rewards">
                <Box>
                  <Controller
                    name="premium_ids"
                    control={control}
                    render={({ field }) => (
                      <PremiumIdInput
                        value={parsePremiumIds(field.value ?? [])}
                        onChange={(entries) => field.onChange(formatPremiumIds(entries))}
                        premiumOptions={premiumOptions}
                        error={errors.premium_ids?.message}
                        data-testid="premium-ids-input"
                        required
                      />
                    )}
                  />
                </Box>
              </CardSection>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
              <CardSection title="Offer Segmentation">
                <Stack spacing={2}>
                  <FieldGroup>
                    <Box sx={{ flex: 1 }}>
                      <FormField label="Min User Age" tooltip={OFFER_FIELD_TOOLTIPS.min_user_age} />
                      <Controller
                        name="min_user_age"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            type="number"
                            fullWidth
                            error={!!errors.min_user_age}
                            helperText={errors.min_user_age?.message || ' '}
                            data-testid="min-age-input"
                            placeholder="0"
                            slotProps={heightProps}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <FormField label="Max User Age" tooltip={OFFER_FIELD_TOOLTIPS.max_user_age} />
                      <Controller
                        name="max_user_age"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            type="number"
                            fullWidth
                            error={!!errors.max_user_age}
                            helperText={errors.max_user_age?.message || ' '}
                            data-testid="max-age-input"
                            placeholder="0"
                            slotProps={heightProps}
                          />
                        )}
                      />
                    </Box>
                  </FieldGroup>

                  <FieldGroup>
                    <Box sx={{ flex: 1 }}>
                      <FormField label="Min LTV" tooltip={OFFER_FIELD_TOOLTIPS.min_ltv} />
                      <Controller
                        name="min_ltv"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            type="number"
                            fullWidth
                            error={!!errors.min_ltv}
                            helperText={errors.min_ltv?.message || ' '}
                            data-testid="min-ltv-input"
                            placeholder="0"
                            slotProps={heightProps}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <FormField label="Max LTV" tooltip={OFFER_FIELD_TOOLTIPS.max_ltv} />
                      <Controller
                        name="max_ltv"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            type="number"
                            fullWidth
                            error={!!errors.max_ltv}
                            helperText={errors.max_ltv?.message || ' '}
                            data-testid="max-ltv-input"
                            placeholder="0"
                            slotProps={heightProps}
                          />
                        )}
                      />
                    </Box>
                  </FieldGroup>

                  <Box>
                    <FormField label="User ID" tooltip={OFFER_FIELD_TOOLTIPS.user_id} />
                    <Controller
                      name="user_id"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={field.value ?? ''}
                          fullWidth
                          error={!!errors.user_id}
                          helperText={errors.user_id?.message || 'Leave empty to make visible to all users'}
                          data-testid="user-id-input"
                          placeholder="e.g., 286 or 052,132,236"
                          slotProps={heightProps}
                        />
                      )}
                    />
                  </Box>
                </Stack>
              </CardSection>
            </Grid>
          </Grid>

          {/* Images & Assets */}
          <CardSection title="Images & Assets">
            <Stack spacing={3}>
              {/* Background Images */}
              <Box>
                <FieldGroup>
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <FormField label="Desktop Background" tooltip={OFFER_FIELD_TOOLTIPS.desktop_background} />
                    <Controller
                      name="desktop_background"
                      control={control}
                      render={({ field }) => (
                        <ImageUpload
                          value={field.value}
                          onChange={field.onChange}
                          offerIdForUpload={isEditing ? Number.parseInt(id) : undefined}
                          uploadFieldName="desktop_background"
                          error={errors.desktop_background?.message}
                          data-testid="desktop-bg-upload"
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <FormField label="Mobile Background" tooltip={OFFER_FIELD_TOOLTIPS.mobile_background} />
                    <Controller
                      name="mobile_background"
                      control={control}
                      render={({ field }) => (
                        <ImageUpload
                          value={field.value}
                          onChange={field.onChange}
                          offerIdForUpload={isEditing ? Number.parseInt(id) : undefined}
                          uploadFieldName="mobile_background"
                          error={errors.mobile_background?.message}
                          data-testid="mobile-bg-upload"
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <FormField label="Popup Background" tooltip={OFFER_FIELD_TOOLTIPS.popup_background} />
                    <Controller
                      name="popup_background"
                      control={control}
                      render={({ field }) => (
                        <ImageUpload
                          value={field.value}
                          onChange={field.onChange}
                          offerIdForUpload={isEditing ? Number.parseInt(id) : undefined}
                          uploadFieldName="popup_background"
                          error={errors.popup_background?.message}
                          data-testid="popup-bg-upload"
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <FormField label="Mobile Icon" tooltip={OFFER_FIELD_TOOLTIPS.mobile_icon} />
                    <Controller
                      name="mobile_icon"
                      control={control}
                      render={({ field }) => (
                        <ImageUpload
                          value={field.value}
                          onChange={field.onChange}
                          offerIdForUpload={isEditing ? Number.parseInt(id) : undefined}
                          uploadFieldName="mobile_icon"
                          error={errors.mobile_icon?.message}
                          data-testid="mobile-icon-upload"
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <FormField label="Popup Icon" tooltip={OFFER_FIELD_TOOLTIPS.popup_icon} />
                    <Controller
                      name="popup_icon"
                      control={control}
                      render={({ field }) => (
                        <ImageUpload
                          value={field.value}
                          onChange={field.onChange}
                          offerIdForUpload={isEditing ? Number.parseInt(id) : undefined}
                          uploadFieldName="popup_icon"
                          error={errors.popup_icon?.message}
                          data-testid="popup-icon-upload"
                        />
                      )}
                    />
                  </Box>
                </FieldGroup>
              </Box>
            </Stack>
          </CardSection>

          {/* Advanced Configuration - Two Column */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
              <CardSection title="Advanced Settings">
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <FormField label="Custom Display Name" tooltip={OFFER_FIELD_TOOLTIPS.custom_name} />
                    <Controller
                      name="custom_name"
                      control={control}
                      render={({ field, fieldState }) => (
                        <JsonEditor
                          name="custom_name"
                          value={field.value}
                          setError={setError}
                          clearErrors={clearErrors}
                          onChange={field.onChange}
                          error={fieldState.error?.message}
                          height={300}
                          data-testid="custom-name-editor"
                        />
                      )}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <FormField label="Trigger Login Reward" tooltip={OFFER_FIELD_TOOLTIPS.trigger_login_reward} />
                    <Controller
                      name="trigger_login_reward"
                      control={control}
                      render={({ field, fieldState }) => (
                        <JsonEditor
                          name="trigger_login_reward"
                          value={field.value}
                          onChange={field.onChange}
                          setError={setError}
                          clearErrors={clearErrors}
                          error={fieldState.error?.message}
                          height={300}
                          data-testid="ab-test-editor"
                        />
                      )}
                    />
                  </Box>
                </Box>
              </CardSection>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
              <CardSection
                title="Inventory Filter"
                helperText={`A configuration used to control offer visibility in the shop based on the user's inventory, ensuring offers are only shown when specific hero upgrade criteria are met. Defines filtering rules for hero upgrade-related offers. The offer will only be shown if all the following conditions are satisfied:`}
              >
                <Stack spacing={3}>
                  <FieldGroup>
                    <Box sx={{ flex: 1 }}>
                      <FormField label="Hero Item ID" tooltip={OFFER_FIELD_TOOLTIPS.hero_item_id} />
                      <Controller
                        name="hero_item_id"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            type="number"
                            fullWidth
                            error={!!errors.hero_item_id}
                            helperText={errors.hero_item_id?.message || ' '}
                            data-testid="hero-item-id-input"
                            placeholder="0"
                            slotProps={heightProps}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <FormField
                        label="Level Item ID"
                        tooltip="Represents the hero’s level. The user must possess this item to show this offer."
                      />
                      <Controller
                        name="level_item_id"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            type="number"
                            fullWidth
                            error={!!errors.level_item_id}
                            helperText={errors.level_item_id?.message || ' '}
                            data-testid="level-item-id-input"
                            placeholder="0"
                            slotProps={heightProps}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <FormField label="Medal Item ID" tooltip={OFFER_FIELD_TOOLTIPS.medal_item_id} />
                      <Controller
                        name="medal_item_id"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            type="number"
                            fullWidth
                            error={!!errors.medal_item_id}
                            helperText={errors.medal_item_id?.message || ' '}
                            data-testid="medal-item-id-input"
                            placeholder="0"
                            slotProps={heightProps}
                          />
                        )}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <FormField label="Required Medals" tooltip={OFFER_FIELD_TOOLTIPS.required_medals} />
                      <Controller
                        name="required_medals"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ''}
                            type="number"
                            fullWidth
                            error={!!errors.required_medals}
                            helperText={errors.required_medals?.message || ' '}
                            data-testid="required-medals-input"
                            placeholder="0"
                            slotProps={heightProps}
                          />
                        )}
                      />
                    </Box>
                  </FieldGroup>
                </Stack>
              </CardSection>
            </Grid>
          </Grid>

          {/* Dynamic Fields and Offer Sync - Side by Side */}
          <Grid container spacing={3}>
            {/* Dynamic Fields (Playbook) */}
            {dynamicFieldList?.length > 0 && (
              <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
                <CardSection title="Dynamic Fields">
                  {dynamicFieldsLoading ? (
                    <Typography variant="body2">Loading fields...</Typography>
                  ) : (
                    <DynamicFieldRenderer
                      control={control}
                      fields={dynamicFieldList}
                      errors={errors}
                      setError={setError}
                      clearErrors={clearErrors}
                    />
                  )}
                </CardSection>
              </Grid>
            )}

            {/* Offer Sync Section - Show in both create and edit mode */}
            <Grid size={{ xs: 12, lg: 6 }} sx={{ display: 'flex' }}>
              <CardSection title="Offer Sync">
                <SyncSection control={control} />
              </CardSection>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" onClick={handleCancel} disabled={isSubmitting} data-testid="cancel-button">
              Cancel
            </Button>
            {!isEditing && (
              <Button
                variant="outlined"
                onClick={() => setSaveTemplateDialogOpen(true)}
                disabled={isSubmitting || !hasFormChanged}
                data-testid="save-template-button"
              >
                Make Template
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting || (!isEditing && !isTemplateSelected && !hasFormChanged)}
              data-testid="save-button"
            >
              {isSubmitting ? 'Saving...' : isEditing ? 'Edit Offer' : 'Create Offer'}
            </Button>
          </Box>
        </form>
      </Box>
      {/* Schedule Drawer */}
      <ScheduleDrawer
        open={scheduleDrawerOpen}
        onClose={() => setScheduleDrawerOpen(false)}
        offer={isEditing && existingOffer ? existingOffer : null}
        mode="inline"
        initialSchedule={scheduleDraft}
        onInlineSave={(schedule) => setScheduleDraft(schedule)}
      />

      {/* Promotion Drawer */}
      <PromotionDrawer
        open={promotionDrawerOpen}
        onClose={() => setPromotionDrawerOpen(false)}
        offer={isEditing && existingOffer ? existingOffer : null}
        mode="inline"
        initialPromotion={promotionDraft}
        onInlineSave={(promotion) => setPromotionDraft(promotion)}
      />

      {/* Sync Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        title={isEditing && syncOnChanged ? 'Confirm Sync Environment' : 'Confirm Offer Creation'}
        message={pendingFormData ? getSyncConfirmationMessage(pendingFormData.sync_on) : ''}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={handleConfirmSync}
        onCancel={handleCancelSync}
        loading={isSubmitting}
        severity={syncOnChanged ? 'warning' : 'info'}
      />

      {/* Save Template Dialog */}
      <SaveTemplateDialog
        open={saveTemplateDialogOpen}
        onClose={() => setSaveTemplateDialogOpen(false)}
        onSave={handleSaveTemplate}
        isSubmitting={createTemplateMutation.isPending}
      />
    </LocalizationProvider>
  );
};

const heightProps = {
  formHelperText: {
    sx: { minHeight: '20px' },
  },
};

export default AddEditOffer;
